import * as React from "react";
import {
  ArrowDownIcon,
  ArrowRightIcon,
  ArrowSquareOutIcon,
  ArrowUpIcon,
  ArrowUpRightIcon,
  ArrowsClockwiseIcon,
  BellIcon,
  BuildingsIcon,
  CalendarIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DownloadSimpleIcon,
  EnvelopeIcon,
  FileTextIcon,
  FlagIcon,
  InfoIcon,
  LockIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  PackageIcon,
  PlusIcon,
  ReceiptIcon,
  TrendDownIcon,
  TrendUpIcon,
  TruckIcon,
  UploadSimpleIcon,
  UserIcon,
  UsersIcon,
  WarningIcon,
  XCircleIcon,
  XIcon,
} from "@phosphor-icons/react";
import type { Icon, IconProps } from "@phosphor-icons/react";
import { toPascalIconName } from "../icons";

/**
 * Icons by name. A small, common set is bundled statically so the usual glyphs
 * paint with the view; every other Phosphor icon is its own lazily loaded
 * chunk, fetched the first time a view names it.
 */
const PRELOADED: Readonly<Record<string, Icon>> = {
  ArrowDown: ArrowDownIcon,
  ArrowRight: ArrowRightIcon,
  ArrowSquareOut: ArrowSquareOutIcon,
  ArrowUp: ArrowUpIcon,
  ArrowUpRight: ArrowUpRightIcon,
  ArrowsClockwise: ArrowsClockwiseIcon,
  Bell: BellIcon,
  Buildings: BuildingsIcon,
  Calendar: CalendarIcon,
  CaretRight: CaretRightIcon,
  Check: CheckIcon,
  CheckCircle: CheckCircleIcon,
  Clock: ClockIcon,
  CurrencyDollar: CurrencyDollarIcon,
  DownloadSimple: DownloadSimpleIcon,
  Envelope: EnvelopeIcon,
  FileText: FileTextIcon,
  Flag: FlagIcon,
  Info: InfoIcon,
  Lock: LockIcon,
  MagnifyingGlass: MagnifyingGlassIcon,
  Minus: MinusIcon,
  Package: PackageIcon,
  Plus: PlusIcon,
  Receipt: ReceiptIcon,
  TrendDown: TrendDownIcon,
  TrendUp: TrendUpIcon,
  Truck: TruckIcon,
  UploadSimple: UploadSimpleIcon,
  User: UserIcon,
  Users: UsersIcon,
  Warning: WarningIcon,
  X: XIcon,
  XCircle: XCircleIcon,
};

const ICON_DIR = "../../../node_modules/@phosphor-icons/react/dist/csr/";

/**
 * One lazy loader per icon file. Vite resolves the glob at build time and
 * emits each icon as its own chunk; nothing here loads until it is called.
 */
const ICON_LOADERS = import.meta.glob<Record<string, Icon>>(
  "../../../node_modules/@phosphor-icons/react/dist/csr/*.es.js",
);

export const ICON_SIZES = { xs: 12, sm: 16, md: 20, lg: 24 } as const;
export type IconSize = keyof typeof ICON_SIZES;

function NoIcon(): null {
  return null;
}

const lazyIcons = new Map<
  string,
  React.LazyExoticComponent<React.ComponentType<IconProps>> | null
>();

function lazyIcon(
  pascal: string,
): React.LazyExoticComponent<React.ComponentType<IconProps>> | null {
  const cached = lazyIcons.get(pascal);
  if (cached !== undefined) return cached;
  const load = ICON_LOADERS[`${ICON_DIR}${pascal}.es.js`];
  const component = load
    ? React.lazy<React.ComponentType<IconProps>>(() =>
        load().then(
          (module) => ({ default: module[`${pascal}Icon`] ?? NoIcon }),
          // A failed chunk fetch must not take the view down with it.
          () => ({ default: NoIcon }),
        ),
      )
    : null;
  lazyIcons.set(pascal, component);
  return component;
}

/** Whether `name` resolves to an icon the renderer can draw. */
export function isKnownIcon(name: string): boolean {
  const pascal = toPascalIconName(name);
  return pascal in PRELOADED || `${ICON_DIR}${pascal}.es.js` in ICON_LOADERS;
}

/**
 * A Phosphor icon by kebab-case or PascalCase name. An unknown name renders
 * nothing, so a view with a bad icon still renders.
 */
export function IconByName({
  name,
  size = "sm",
  className,
}: {
  name: string;
  size?: IconSize;
  className?: string;
}) {
  const pascal = toPascalIconName(name);
  const px = ICON_SIZES[size];
  const preloaded = PRELOADED[pascal];
  if (preloaded) {
    const Preloaded = preloaded;
    return <Preloaded size={px} className={className} aria-hidden="true" />;
  }
  const Lazy = lazyIcon(pascal);
  if (!Lazy) return null;
  return (
    <React.Suspense
      fallback={
        <span
          aria-hidden="true"
          className={className}
          style={{ display: "inline-block", width: px, height: px }}
        />
      }
    >
      <Lazy size={px} className={className} aria-hidden="true" />
    </React.Suspense>
  );
}
